import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ReceiptText, Calculator } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './IncomeTaxCalculatorPage/components/CalculationForm';
import ResultsOverview from './IncomeTaxCalculatorPage/components/ResultsOverview';
import DeductionsSection from './IncomeTaxCalculatorPage/components/DeductionsSection';
import TaxBreakdownSection from './IncomeTaxCalculatorPage/components/TaxBreakdownSection';
import QuickAdjustmentSidebar from './IncomeTaxCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './IncomeTaxCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './IncomeTaxCalculatorPage/components/TipsSidebar';
import InfoContent from './IncomeTaxCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  incomeType: 'salary',
  incomeAmount: '1000000', // Годовой доход
  paymentPeriod: 'annual', // 'annual', 'monthly', 'daily'
  includeDeductions: false,
  taxResidency: 'resident', // 'resident', 'nonresident'
  hasChildren: false,
  childrenCount: '1',
  hasChildrenDisabled: false,
  childrenDisabledCount: '0',
  hasIpn: false, // Индивидуальный пенсионный накопительный счет
  ipnAmount: '0',
  hasMortgage: false,
  mortgageInterestAmount: '0',
  hasEducation: false,
  educationExpenseAmount: '0',
  hasMedical: false,
  medicalExpenseAmount: '0',
  hasInsurance: false,
  insuranceExpenseAmount: '0',
  hasInvestment: false,
  investmentExpenseAmount: '0',
  hasCharity: false,
  charityExpenseAmount: '0',
  isIp: false, // Индивидуальный предприниматель
  ipTaxSystem: 'osn', // 'osn', 'usn_income', 'usn_income_expenses', 'psn', 'eshn', 'patent'
  ipExpenses: '0',
  isSelfEmployed: false,
  region: 'moscow',
  year: '2024'
};

// Определение типов доходов
export const incomeTypes = [
  { id: 'salary', name: 'Заработная плата', description: 'Доход от работы по трудовому договору' },
  { id: 'business', name: 'Доход от предпринимательской деятельности', description: 'Доход от ведения бизнеса (ИП)' },
  { id: 'dividend', name: 'Дивиденды', description: 'Доход от владения акциями российских компаний' },
  { id: 'foreign_dividend', name: 'Дивиденды от иностранных компаний', description: 'Доход от владения акциями зарубежных компаний' },
  { id: 'interest', name: 'Проценты по вкладам', description: 'Доход от банковских вкладов и счетов' },
  { id: 'rent', name: 'Доход от аренды имущества', description: 'Доход от сдачи недвижимости в аренду' },
  { id: 'sale_property', name: 'Продажа имущества', description: 'Доход от продажи недвижимости, транспорта и т.д.' },
  { id: 'gift', name: 'Подарки', description: 'Доход в виде подарков (не от близких родственников)' },
  { id: 'foreign_income', name: 'Доход из-за рубежа', description: 'Доход, полученный за пределами РФ' },
  { id: 'other', name: 'Прочие доходы', description: 'Другие виды доходов, облагаемые НДФЛ' }
];

// Налоговые ставки НДФЛ в России
export const taxRates = {
  'resident': {
    'salary': {
      'progressive': [
        { threshold: 0, rate: 13 },
        { threshold: 5000000, rate: 15 }
      ],
      'flat': 13
    },
    'business': {
      'osn': 13,
      'usn_income': 6,
      'usn_income_expenses': 15,
      'eshn': 6,
      'psn': 6,
      'patent': 6
    },
    'self_employed': {
      'individual': 4,
      'legal_entity': 6
    },
    'dividend': 13,
    'foreign_dividend': 13,
    'interest': 13,
    'rent': 13,
    'sale_property': 13,
    'gift': 13,
    'foreign_income': 13,
    'other': 13
  },
  'nonresident': {
    'salary': 15,
    'business': 15,
    'dividend': 15,
    'foreign_dividend': 15,
    'interest': 15,
    'rent': 15,
    'sale_property': 15,
    'gift': 15,
    'foreign_income': 15,
    'other': 15
  }
};

// Лимиты вычетов
export const deductionLimits = {
  'standard': {
    'children': {
      'regular': 1400, // на первого и второго ребенка (в месяц)
      'third_plus': 3000, // на третьего и последующих детей (в месяц)
      'disabled': 12000, // на ребенка-инвалида (в месяц)
      'income_limit': 350000 // применяется до достижения дохода (за год)
    }
  },
  'social': {
    'education': 50000, // за обучение (в год)
    'medical': 120000, // за лечение (в год, общий лимит с другими социальными вычетами)
    'expensive_medical': 'unlimited', // дорогостоящее лечение
    'insurance': 120000, // добровольное страхование (в год, общий лимит с другими социальными вычетами)
    'ipn': 400000, // индивидуальный пенсионный накопительный счет (в год)
    'charity': '25%' // благотворительность (до 25% годового дохода)
  },
  'property': {
    'house_purchase': 2000000, // при покупке жилья (разовый)
    'mortgage_interest': 3000000, // проценты по ипотеке (разовый)
    'sale_house_less_5_years': 1000000, // продажа недвижимости в собственности менее 5 лет (разовый)
    'sale_house_more_5_years': 'unlimited', // продажа недвижимости в собственности более 5 лет
    'sale_other_less_3_years': 250000, // продажа другого имущества в собственности менее 3 лет (разовый)
    'sale_other_more_3_years': 'unlimited' // продажа другого имущества в собственности более 3 лет
  },
  'investment': {
    'iis_type_a': 400000, // инвестиционный вычет типа А (в год)
    'iis_type_b': 'unlimited', // инвестиционный вычет типа Б
    'long_term_securities': 'unlimited' // долгосрочное владение ценными бумагами
  }
};

/**
 * IncomeTaxCalculatorPage Component - Полный калькулятор НДФЛ с учетом различных типов доходов и налоговых вычетов.
 */
const IncomeTaxCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('income-tax-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор НДФЛ | Gensio';
  const metaDescription = tool?.metaDescription || 'Точный расчет НДФЛ с различными ставками и налоговыми вычетами: стандартные, социальные, имущественные, инвестиционные вычеты. Калькулятор НДФЛ для физических лиц и ИП с учетом всех особенностей налогообложения в России.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [taxDetails, setTaxDetails] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedCalcsData = localStorage.getItem('savedTaxCalculations');
    if (savedCalcsData) {
      try {
        setSavedCalculations(JSON.parse(savedCalcsData));
      } catch (e) {
        console.error("Error loading saved tax calculations:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'salary':
          newState.incomeType = 'salary';
          break;
        case 'entrepreneur':
          newState.isIp = true;
          newState.incomeType = 'business';
          break;
        case 'investment':
          newState.incomeType = 'dividend';
          newState.hasInvestment = true;
          break;
        case 'property':
          newState.incomeType = 'sale_property';
          break;
        case 'deductions':
          newState.includeDeductions = true;
          break;
        case 'self-employed':
          newState.isSelfEmployed = true;
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setTaxDetails(null);
    setErrors({});
  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalc = {
      id: Date.now().toString(),
      name: name || `Расчет НДФЛ: ${formData.incomeAmount}₽ (${formData.incomeType})`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      taxDetails: { ...taxDetails }
    };

    const updatedSavedCalcs = [...savedCalculations, newSavedCalc];
    setSavedCalculations(updatedSavedCalcs);
    localStorage.setItem('savedTaxCalculations', JSON.stringify(updatedSavedCalcs));

    return newSavedCalc;
  };

  // Удаление сохраненного расчета
  const deleteSavedCalculation = (id) => {
    const updatedSavedCalcs = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedSavedCalcs);
    localStorage.setItem('savedTaxCalculations', JSON.stringify(updatedSavedCalcs));
  };

  // Загрузка сохраненного расчета
  const loadSavedCalculation = (id) => {
    const calcToLoad = savedCalculations.find(calc => calc.id === id);
    if (calcToLoad) {
      setFormData(calcToLoad.formData);
      setResults(calcToLoad.results);
      setTaxDetails(calcToLoad.taxDetails);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <ReceiptText className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор НДФЛ'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              taxDetails={taxDetails}
              setTaxDetails={setTaxDetails}
              incomeTypes={incomeTypes}
              taxRates={taxRates}
              deductionLimits={deductionLimits}
              saveCalculation={saveCalculation}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} formData={formData} />
                
                {/* Показывать детали вычетов только если они используются */}
                {formData.includeDeductions && (
                  <DeductionsSection 
                    taxDetails={taxDetails} 
                    formData={formData}
                    deductionLimits={deductionLimits}
                  />
                )}
                
                {/* Детальная разбивка налога */}
                <TaxBreakdownSection 
                  taxDetails={taxDetails}
                  results={results}
                  formData={formData}
                />
              </>
            )}

            {/* --- Информационный контент о НДФЛ --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
            />

            {/* --- Панель сравнения расчетов --- */}
            <ComparisonSidebar 
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }
      `}</style>
    </div>
  );
};

export default IncomeTaxCalculatorPage;